import {
  DisplayMode,
  type SectionIn,
  type SectionOut,
} from '@/api/generated/schemas';
import { normalizeOrderedRows, type OrderedRow } from '../orderedRows';

/** Form.List row of a sections editor: an ordered collection plus the mode
 * the client presents it in. */
export type SectionRow = OrderedRow<'collection_id'> & {
  display_mode?: DisplayMode | null;
};

/** Row keys the payload must carry beyond the id and the position. */
const SECTION_EXTRA_FIELDS = ['display_mode'] as const;

/** Seed for a row added from the editor's add button. Without it a fresh row
 * would have no mode to show until the server answered. */
export const SECTION_ROW_DEFAULTS = { display_mode: DisplayMode.regular };

/**
 * Server rows in display order, in the shape the editor forms take. Drops
 * the nested collection and the timestamps: the forms never read them, and
 * seeding them into form state only risks sending them back.
 */
export const sectionRowsFromRecord = (
  sections: SectionOut[] | undefined | null
): SectionRow[] =>
  [...(sections ?? [])]
    .sort((a, b) => a.position - b.position)
    .map((section) => ({
      id: section.id,
      collection_id: section.collection_id,
      display_mode: section.display_mode,
    }));

/**
 * Upsert payload for `CategoryUpdateIn.sections`. The one way the three
 * editors are allowed to build it — a plain `normalizeOrderedRows` would drop
 * `display_mode`, and saving an untouched form would reset every section to
 * regular.
 */
export const normalizeSectionRows = (
  rows: ReadonlyArray<SectionRow> | undefined | null
): SectionIn[] =>
  normalizeOrderedRows(
    rows,
    'collection_id',
    SECTION_EXTRA_FIELDS
  ) as unknown as SectionIn[];
